// Replays (§23.6, §9.5, §9.7, §11): plays a ReplaySlice as a FrameView source. The slice is
// interpolated along a playback-rate curve, its events are re-dispatched on the bus (flagged
// `replay`) when the playhead crosses their tick, and the live source keeps being stepped
// underneath, taking over when the replay completes or is skipped. WP7 owns it.
// The VHS rewind transition (§9.5, §11): before playing, the slice scrubs backwards from its end to
// its start (0.6 s; 0.2 s for BONK CAM, which must fit before the ghost appears, §2.12) with
// `replay.t` negative, so the camera, the FinalPass tear and the DOM overlay show a tape rewinding.
// No events are dispatched while rewinding. DOM- and three-free.
import { MAX_BEANS, TICK_HZ, TUNE } from '@shared/constants';
import { EV, KoFlag, type SimEvent } from '@shared/events';
import { roundClock } from '@shared/maps/clock';
import { MAPS } from '@shared/maps/index';
import { applySnapshotToWorld, type Snapshot } from '@shared/protocol/snapshot';
import type { SnapEnt } from '@shared/protocol/schema';
import { createWorld } from '@shared/sim/world';
import type { World } from '@shared/types';
import type { Bus } from '../../core/bus';
import { FrameTimer } from '../../core/time';
import {
  copyMapState, createFrameView, fillModeView, resizeEntityViews, setBeanIdentity, setRoundInfo,
  type FrameSource, type FrameView, type ReplayKind, type ReplayView,
} from '../../game/FrameView';
import { INTERPOLATION, sampleBean, sampleEntity } from '../../game/Interpolation';
import type { ReplaySlice } from '../../game/ReplayBuffer';

export const REPLAY = {
  /** Instant Replay and Play of the Match run at 1.0×, dropping to 0.35× within ±0.4 s of the KO. */
  slowRate: 0.35,
  slowTicks: 24,
  /** BONK CAM: 0.6× (§23.6) for 2.0 s (§11), so it ends before the ghost appears (§2.12). */
  bonkCam: { rate: 0.6, durationSec: 2.0 },
  /** VHS rewind wipe before playback, seconds per kind. */
  rewindSec: { instant: 0.6, bonkcam: 0.2, potm: 0.7 },
} as const;

export interface ReplayOptions {
  /** The live source the replay covers: stepped every frame underneath it, shown again after it. */
  under: FrameSource;
  /** Play the VHS rewind wipe first (default true). */
  rewind?: boolean;
}

/** A replay in progress, itself a FrameView source. */
export interface ReplaySource extends FrameSource {
  readonly kind: ReplayKind;
  /** Playback length in seconds (after the rewind wipe). */
  readonly duration: number;
  /** Length of the rewind wipe before playback, seconds (0 without one). */
  readonly rewindSec: number;
  /** Completed or skipped: `frame` returns the live view from then on. */
  readonly done: boolean;
  skip(): void;
}

/** Ticks of [lo, hi) at the slow rate around `koTick`; outside it the rate is 1 (BONK CAM: constant). */
interface RateCurve { constant: number; lo: number; hi: number }

function curveFor(kind: ReplayKind, koTick: number): RateCurve {
  if (kind === 'bonkcam') return { constant: REPLAY.bonkCam.rate, lo: Infinity, hi: Infinity };
  return { constant: 0, lo: koTick - REPLAY.slowTicks, hi: koTick + REPLAY.slowTicks };
}

function rateAt(c: RateCurve, tick: number): number {
  if (c.constant > 0) return c.constant;
  return tick >= c.lo && tick < c.hi ? REPLAY.slowRate : 1;
}

/** The first tick after `tick` where the rate changes. */
function nextChange(c: RateCurve, tick: number): number {
  if (c.constant > 0 || tick >= c.hi) return Infinity;
  return tick < c.lo ? c.lo : c.hi;
}

/** Moves `tick` forward by `seconds` of playback, never past `end`. */
function advancePlayhead(c: RateCurve, tick: number, seconds: number, end: number): number {
  let t = tick;
  let left = seconds;
  while (left > 0 && t < end) {
    const rate = rateAt(c, t);
    const stop = Math.min(nextChange(c, t), end);
    const need = (stop - t) / (TICK_HZ * rate);
    if (need > left) return t + left * TICK_HZ * rate;
    t = stop;
    left -= need;
  }
  return Math.min(t, end);
}

/** Playback seconds from `from` to `to`. */
function playbackSeconds(c: RateCurve, from: number, to: number): number {
  let t = from;
  let s = 0;
  while (t < to) {
    const rate = rateAt(c, t);
    const stop = Math.min(nextChange(c, t), to);
    s += (stop - t) / (TICK_HZ * rate);
    t = stop;
  }
  return s;
}

/** The focus bean's KO in the slice closest to where its window puts it; that estimate if none. */
function findKoTick(slice: ReplaySlice, kind: ReplayKind): number {
  const expected = kind === 'bonkcam' ? slice.endTick : slice.endTick - TUNE.highlight.postTicks;
  let best = expected;
  let bestDist = Infinity;
  for (const s of slice.snapshots) {
    for (const ev of s.events) {
      if (ev.type !== EV.KO || ev.a !== slice.focusSlot || ev.tick < slice.startTick || ev.tick > slice.endTick) continue;
      const d = Math.abs(ev.tick - expected);
      if (d < bestDist) {
        best = ev.tick;
        bestDist = d;
      }
    }
  }
  return best;
}

/** The slice's KO of its focus bean carries the round's FINAL flag. */
export function isFinalKo(slice: ReplaySlice): boolean {
  for (const s of slice.snapshots) {
    for (const ev of s.events) {
      if (ev.type === EV.KO && ev.a === slice.focusSlot && (ev.d & KoFlag.FINAL) !== 0) return true;
    }
  }
  return false;
}

/** A replay that was declined: already done, its frames are the live source's. */
function skippedReplay(kind: ReplayKind, under: FrameSource): ReplaySource {
  return {
    kind, duration: 0, rewindSec: 0, done: true,
    skip() { /* nothing to skip */ },
    frame: (now) => under.frame(now),
  };
}

function findEnt(ents: readonly SnapEnt[], id: number): SnapEnt | null {
  for (const e of ents) if (e.id === id) return e;
  return null;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

class SliceReplay implements ReplaySource {
  readonly duration: number;
  readonly rewindSec: number;
  private rewindLeft: number;
  private readonly view: FrameView = createFrameView();
  private readonly replay: ReplayView;
  private readonly curve: RateCurve;
  private readonly world: World;
  private readonly events: SimEvent[];
  private readonly timer = new FrameTimer();
  private readonly start: number;
  private head: number;
  private nextEvent = 0;
  /** Index of bracket snapshot A in the slice; it only moves forward. */
  private cursor = 0;
  private appliedSnap: Snapshot | null = null;
  private elapsed = 0;
  private ended = false;

  constructor(
    private readonly bus: Bus,
    private readonly slice: ReplaySlice,
    readonly kind: ReplayKind,
    private readonly under: FrameSource,
    private readonly onEnd: (r: SliceReplay) => void,
    rewind: boolean,
  ) {
    this.curve = curveFor(kind, findKoTick(slice, kind));
    const bonk = REPLAY.bonkCam;
    this.start = kind === 'bonkcam' ? Math.max(slice.startTick, slice.endTick - bonk.durationSec * bonk.rate * TICK_HZ) : slice.startTick;
    this.head = this.start;
    this.duration = playbackSeconds(this.curve, this.start, slice.endTick);
    this.events = slice.snapshots
      .flatMap((s) => s.events)
      .filter((e) => e.tick >= this.start && e.tick <= slice.endTick)
      .sort((x, y) => x.tick - y.tick);
    this.world = createWorld(slice.cfg, slice.roundId, false);
    this.rewindSec = rewind ? REPLAY.rewindSec[kind] : 0;
    this.rewindLeft = this.rewindSec;
    this.replay = { kind, t: -this.rewindSec, duration: this.duration, focusSlot: slice.focusSlot, killerSlot: slice.killerSlot };
    this.view.source = 'replay';
    setRoundInfo(this.view, slice.cfg, slice.roundId);
  }

  get done(): boolean {
    return this.ended;
  }

  skip(): void {
    this.finish();
  }

  frame(now: number): FrameView {
    const live = this.under.frame(now);
    if (this.ended) return live;
    const dt = this.timer.frame(now);
    if (this.rewindLeft > 0) {
      // The tape scrubs backwards from the slice end to the playback start, easing in and out.
      this.rewindLeft = Math.max(0, this.rewindLeft - dt);
      const k = smooth(this.rewindLeft / this.rewindSec);
      this.head = this.start + (this.slice.endTick - this.start) * k;
      return this.build(live, now, 0, 1, -this.rewindLeft);
    }
    const prev = this.head;
    this.head = advancePlayhead(this.curve, this.head, dt, this.slice.endTick);
    this.elapsed = Math.min(this.duration, this.elapsed + dt);
    this.dispatchDue();
    if (this.head >= this.slice.endTick) {
      this.finish();
      return live;
    }
    // The view's dt is the game time this frame covered (§23.2: already scaled).
    return this.build(live, now, (this.head - prev) / TICK_HZ, rateAt(this.curve, this.head), this.elapsed);
  }

  /** Stops the replay (idempotent); the remaining events are not dispatched. */
  finish(): void {
    if (this.ended) return;
    this.ended = true;
    this.onEnd(this);
  }

  private dispatchDue(): void {
    const evs = this.events;
    while (this.nextEvent < evs.length && evs[this.nextEvent].tick <= this.head) {
      this.bus.emit('sim', { ev: evs[this.nextEvent++], local: false, replay: true });
    }
  }

  private build(live: FrameView, now: number, dt: number, rate: number, playT: number): FrameView {
    const v = this.view;
    const slice = this.slice;
    const cfg = slice.cfg;
    v.now = now;
    v.dt = dt;
    v.timeScale = rate;
    v.roomPhase = live.roomPhase;
    v.roundIndex = live.roundIndex;
    v.totalRounds = live.totalRounds;
    v.roundTick = this.head;
    v.renderTick = v.localTick = slice.startRoomTick + this.head;
    roundClock(cfg.mapId, cfg.modeId, this.head, cfg.capTickOverride, v.clock);
    v.localSlot = live.localSlot;
    v.focusSlot = slice.focusSlot;

    const snaps = slice.snapshots;
    while (this.cursor > 0 && snaps[this.cursor].roundTick > this.head) this.cursor--;
    while (this.cursor + 1 < snaps.length && snaps[this.cursor + 1].roundTick <= this.head) this.cursor++;
    const a = snaps[this.cursor];
    const b = this.cursor + 1 < snaps.length && a.roundTick <= this.head ? snaps[this.cursor + 1] : null;
    const t = b ? (this.head - a.roundTick) / (b.roundTick - a.roundTick) : 0;
    const since = b ? this.head - a.roundTick : Math.min(Math.max(0, this.head - a.roundTick), INTERPOLATION.maxExtrapolateTicks);

    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const bv = v.beans[slot];
      const rec = a.beans[slot];
      if (rec) sampleBean(bv, rec, b ? b.beans[slot] : null, t, since);
      else bv.active = false;
      setBeanIdentity(bv, live.beans[slot]);
      bv.isLocal = slot === live.localSlot;
      bv.crowned = slot === cfg.crownedSlot;
    }
    resizeEntityViews(v.ents, a.ents.length);
    for (let i = 0; i < a.ents.length; i++) sampleEntity(v.ents[i], a.ents[i], b ? findEnt(b.ents, a.ents[i].id) : null, t, since);

    if (this.appliedSnap !== a) {
      applySnapshotToWorld(this.world, a);
      this.appliedSnap = a;
    }
    copyMapState(v.map, a.map);
    MAPS[cfg.mapId].hazardPose(this.world, this.head, v.hazard);
    fillModeView(v, a.mode);
    Object.assign(v.net, live.net);
    this.replay.t = playT;
    v.replay = this.replay;
    v.grade = v.golden ? 'gold' : 'normal';
    return v;
  }
}

/** Plays one replay at a time; `'replay:start'` / `'replay:end'` bracket each one on the bus. */
export class ReplayPlayer {
  private active: SliceReplay | null = null;

  constructor(private readonly bus: Bus) {}

  /** The replay in progress, or null. */
  get current(): ReplaySource | null {
    return this.active;
  }

  /** Starts `slice` (ending any replay in progress) and returns it as the frame source to use. */
  play(slice: ReplaySlice, kind: ReplayKind, opts: ReplayOptions): ReplaySource {
    // A BONK CAM of the round's final KO is not played: that KO already gets the live final-KO slow
    // motion (or GAME!), and the Instant Replay / Play of the Match shows it again right after.
    if (kind === 'bonkcam' && isFinalKo(slice)) return skippedReplay(kind, opts.under);
    this.active?.finish();
    const replay = new SliceReplay(this.bus, slice, kind, opts.under, (r) => {
      if (this.active === r) this.active = null;
      this.bus.emit('replay:end', { kind: r.kind });
    }, opts.rewind !== false);
    this.active = replay;
    this.bus.emit('replay:start', { kind });
    return replay;
  }

  /** Skips the replay in progress, if any. */
  skip(): void {
    this.active?.finish();
  }
}
